import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from '@mui/icons-material';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import { harmonateCardSx } from '../../../theme/harmonateStyles';

const brandGreen = ht.green;
const brandGreenDark = ht.green;
const brandGreenBorder = 'rgba(2, 107, 29, 0.22)';
const brandGreenSoft = 'rgba(2, 107, 29, 0.06)';

const formatCount = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US');
};

const formatPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}%`;
};

const formatHours = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const PMS_ROWS = [
  { key: 'arrivals', label: 'Arrivals', format: formatCount },
  { key: 'inHouseGuests', label: 'In-House Guests', format: formatCount },
  { key: 'departures', label: 'Departures', format: formatCount },
  { key: 'occupancyPercent', label: 'Occupancy %', format: formatPercent },
  { key: 'breakfastCount', label: 'Breakfast Count', format: formatCount },
  { key: 'eventCount', label: 'Event Count', format: formatCount },
  { key: 'conferenceCount', label: 'Conference Count', format: formatCount },
];

const LABOR_ROWS = [
  { key: 'scheduledLaborHours', label: 'Scheduled Labour Hours', format: formatHours, bg: '#E3F2FD' },
  { key: 'scheduledLaborCost', label: 'Scheduled Labor Cost', format: formatCurrency, bg: '#E8F5E9' },
  { key: 'forecastedHours', label: 'Forecasted Hours', format: formatHours, bg: '#F3E5F5' },
  { key: 'forecastedLaborCost', label: 'Forecasted Labor Cost', format: formatCurrency, bg: '#FCE4EC' },
  { key: 'budgetedHours', label: 'Budgeted Hours', format: formatHours, bg: '#FFF8E1' },
  { key: 'budgetedLaborCost', label: 'Budgeted Labor Cost', format: formatCurrency, bg: '#FFF3E0' },
];

const SummaryGridTable = ({ title, rows, days, weekDates, expanded, onToggle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        py: 0.5,
      }}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onToggle();
      }}
    >
      <IconButton size="small" aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`} sx={{ color: brandGreenDark }}>
        {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
      </IconButton>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: ht.text }}>
        {title}
      </Typography>
    </Box>
    <Collapse in={expanded}>
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: 220,
          border: `1px solid ${brandGreenBorder}`,
          borderRadius: ht.cardRadius,
          bgcolor: ht.white,
        }}
      >
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: brandGreenSoft }}>
              <TableCell sx={{ fontWeight: 700, minWidth: 180, borderRight: `1px solid ${brandGreenBorder}` }} />
              {weekDates.map((d, idx) => (
                <TableCell
                  key={idx}
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    borderRight: idx < 6 ? `1px solid ${brandGreenBorder}` : undefined,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key} sx={{ bgcolor: row.bg || 'transparent' }}>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    borderRight: `1px solid ${brandGreenBorder}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.label}
                </TableCell>
                {days.map((day, idx) => (
                  <TableCell
                    key={`${row.key}-${idx}`}
                    align="center"
                    sx={{
                      fontSize: '0.8rem',
                      borderRight: idx < 6 ? `1px solid ${brandGreenBorder}` : undefined,
                    }}
                  >
                    {row.format(day?.[row.key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Collapse>
  </Box>
);

const WorkforceWeekSummaryPanels = ({
  tenantId,
  propertyId,
  weekStartDate,
  weekDates,
  refreshKey = 0,
  defaultPanelsExpanded = false,
  embeddedInPaper = false,
}) => {
  const [panelsExpanded, setPanelsExpanded] = useState(defaultPanelsExpanded);
  const [pmsExpanded, setPmsExpanded] = useState(true);
  const [laborExpanded, setLaborExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState([]);

  const fetchSummary = useCallback(async () => {
    if (!tenantId || !propertyId || !weekStartDate) {
      setDays([]);
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const url =
        `${base}conductor/workforce-week-summary?tenant_id=${encodeURIComponent(tenantId)}` +
        `&property_id=${encodeURIComponent(propertyId)}` +
        `&week_start_date=${encodeURIComponent(weekStartDate)}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed to load week summary (${res.status})`);
      }
      const data = await res.json();
      setDays(Array.isArray(data?.days) ? data.days : []);
    } catch (err) {
      console.error(err);
      setDays([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, propertyId, weekStartDate]);

  useEffect(() => {
    if (panelsExpanded) {
      fetchSummary();
    }
  }, [panelsExpanded, fetchSummary, refreshKey]);

  const orderedDays = useMemo(() => {
    if (!days.length) {
      return (weekDates || []).map((d) => ({
        businessDate: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
      }));
    }
    return days;
  }, [days, weekDates]);

  if (!propertyId) {
    return null;
  }

  const content = (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          mb: panelsExpanded ? 1 : 0,
        }}
        onClick={() => setPanelsExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setPanelsExpanded((v) => !v);
        }}
      >
        <IconButton
          size="small"
          aria-label={panelsExpanded ? 'Collapse forecast and labor panels' : 'Expand forecast and labor panels'}
          sx={{ color: brandGreenDark }}
        >
          {panelsExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
          PMS Forecast &amp; Labor Summary
        </Typography>
        {loading ? <CircularProgress size={18} sx={{ ml: 1, color: brandGreen }} /> : null}
      </Box>

      <Collapse in={panelsExpanded}>
        <SummaryGridTable
          title="Forecast Demand (PMS)"
          rows={PMS_ROWS}
          days={orderedDays}
          weekDates={weekDates}
          expanded={pmsExpanded}
          onToggle={() => setPmsExpanded((v) => !v)}
        />
        <SummaryGridTable
          title="Labor & Budget"
          rows={LABOR_ROWS}
          days={orderedDays}
          weekDates={weekDates}
          expanded={laborExpanded}
          onToggle={() => setLaborExpanded((v) => !v)}
        />
      </Collapse>
    </>
  );

  return (
    <Box sx={{ mb: 2 }}>
      {embeddedInPaper ? (
        <Paper
          elevation={0}
          sx={{
            ...harmonateCardSx,
            bgcolor: ht.white,
            p: 2,
          }}
        >
          {content}
        </Paper>
      ) : (
        content
      )}
    </Box>
  );
};

export default WorkforceWeekSummaryPanels;
